import { Container } from 'pixi.js';
import type { Application } from './Application';
import type { SceneManager } from './SceneManager';
import type { InputManager } from './InputManager';
import type { Camera } from './Camera';

export interface SceneContext {
  app: Application;
  sceneManager: SceneManager;
  input: InputManager;
  camera: Camera;
}

export interface SceneData {
  [key: string]: unknown;
}

/**
 * Base class for all game scenes.
 * Extends PIXI.Container and provides lifecycle methods for scene management.
 */
export abstract class Scene extends Container {
  public readonly key: string;
  protected context!: SceneContext;
  protected data: SceneData = {};
  private _isActive: boolean = false;
  private _isVisible: boolean = true;

  constructor(key: string) {
    super();
    this.key = key;
    this.sortableChildren = true;
  }

  /**
   * Initialize the scene with context (called by SceneManager)
   */
  init(context: SceneContext): void {
    this.context = context;
  }

  /**
   * Set scene data (passed when starting/launching the scene)
   */
  setData(data: SceneData): void {
    this.data = { ...this.data, ...data };
  }

  // Convenience getters
  protected get app(): Application {
    return this.context.app;
  }

  protected get sceneManager(): SceneManager {
    return this.context.sceneManager;
  }

  protected get input(): InputManager {
    return this.context.input;
  }

  protected get camera(): Camera {
    return this.context.camera;
  }

  protected get game() {
    return {
      events: this.context.sceneManager.events,
    };
  }

  protected get scene() {
    return {
      start: (key: string, data?: SceneData) => this.sceneManager.start(key, data),
      launch: (key: string, data?: SceneData) => this.sceneManager.launch(key, data),
      stop: (key: string) => this.sceneManager.stop(key),
      get: (key: string) => this.sceneManager.getScene(key),
      isActive: (key: string) => this.sceneManager.isActive(key),
    };
  }

  // State getters
  get isActive(): boolean {
    return this._isActive;
  }

  get isVisible(): boolean {
    return this._isVisible;
  }

  /**
   * Called once when the scene is first created.
   * Override to set up scene contents.
   */
  abstract create(): void | Promise<void>;

  /**
   * Called every frame while the scene is active.
   * @param delta - Time elapsed since last frame (in ms, normalized to ~16.67ms at 60fps)
   */
  abstract update(delta: number): void;

  /**
   * Called when the scene is being shut down.
   * Override to clean up resources.
   */
  shutdown(): void {
    // Override in subclasses for cleanup
  }

  /**
   * Internal: Set scene active state
   */
  _setActive(active: boolean): void {
    this._isActive = active;
  }

  /**
   * Internal: Set scene visibility
   */
  _setVisible(visible: boolean): void {
    this._isVisible = visible;
    this.visible = visible;
  }

  /**
   * Called by SceneManager to destroy the scene
   */
  destroyScene(): void {
    this.shutdown();
    this._isActive = false;
    this.removeFromParent();
    this.destroy({ children: true });
  }
}

// Scene constructor type for registration
export type SceneConstructor = new (key: string) => Scene;
